import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import authService from "../services/authService";

const Navbar = ({ toggleSidebar }) => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const handleLogout = () => {
    authService.logout();
    toast.info("Logged out successfully.");
    navigate("/login");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const roleLabel =
    {
      SUPER_ADMIN: "SUPER ADMIN",
      DEALER: "MASTER DEALER",
      SHOP: "SHOP OWNER",
      PLAYER: "PLAYER",
    }[user?.role] || user?.role;

  const initials = (user?.full_name || user?.username || "U")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return (
    <nav className="app-navbar sticky-top bg-[#0D111C] border-b border-[rgba(148,163,184,0.14)] z-50">
      <div className="container-fluid h-100 px-3 px-lg-4">
        <div className="d-flex align-items-center justify-content-between h-100">
          {/* Left section: Mobile toggle & ROYAL BET brand */}
          <div className="d-flex align-items-center gap-2 gap-md-3">
            <button
              className="app-icon-btn d-md-none text-[#F8FAFC] p-2 rounded-lg bg-[#121827] border border-[rgba(148,163,184,0.14)]"
              onClick={toggleSidebar}
              aria-label="Toggle sidebar"
              type="button"
            >
              <i className="bi bi-list fs-5" />
            </button>

            <div
              className="app-brand d-flex align-items-center gap-2.5 cursor-pointer"
              role="button"
              onClick={() => navigate("/dashboard")}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/35 text-lg font-bold shadow-md">
                ♠
              </div>
              <div className="d-flex flex-column leading-none">
                <span className="text-xs font-black tracking-wider text-[#F8FAFC]">
                  ROYAL BET
                </span>
                <span className="text-[9px] font-bold tracking-widest text-[#D4AF37]">
                  CONTROL CENTER
                </span>
              </div>
            </div>
          </div>

          {/* Character Avatar & Dropdown Profile / Logout Menu */}
          {user && (
            <div className="position-relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((prev) => !prev)}
                className="d-flex align-items-center gap-2.5 bg-[#121827] border border-[rgba(148,163,184,0.18)] hover:border-[#D4AF37]/50 rounded-full py-1.5 px-2.5 transition-all focus:outline-none"
                title="Account Profile & Logout"
              >
                {/* Character Avatar Circle Icon */}
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-tr from-[#D4AF37] to-[#F4D77A] text-[#070A12] font-black text-lg shadow-md shadow-[#D4AF37]/20 border border-[#F4D77A]/40 overflow-hidden">
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.username}
                      className="h-full w-full rounded-full object-cover"
                    />
                  ) : (
                    <i className="bi bi-person-fill text-lg text-[#070A12]" />
                  )}
                </div>

                <div className="d-none d-sm-block text-start me-1">
                  <div className="text-xs font-bold text-[#F8FAFC] leading-tight">
                    {user.full_name || user.username}
                  </div>
                  <div className="text-[10px] font-semibold text-[#D4AF37] leading-tight">
                    {roleLabel}
                  </div>
                </div>

                <i
                  className={`bi bi-chevron-down text-xs text-[#94A3B8] transition-transform duration-200 ${
                    menuOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Dropdown Menu on Character Avatar Click */}
              {menuOpen && (
                <div
                  className="position-absolute end-0 mt-2 bg-[#121827] border border-[rgba(148,163,184,0.2)] shadow-2xl rounded-2xl p-4 text-[#F8FAFC] z-50 animate-in fade-in slide-in-from-top-2 duration-150"
                  style={{ minWidth: "240px" }}
                >
                  <div className="d-flex align-items-center gap-3 pb-3 border-b border-[rgba(148,163,184,0.14)] mb-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-tr from-[#D4AF37] to-[#F4D77A] text-[#070A12] text-xl font-black shadow-md">
                      <i className="bi bi-person-fill text-xl text-[#070A12]" />
                    </div>
                    <div className="overflow-hidden">
                      <div className="fw-bold text-[#F8FAFC] text-sm text-truncate">
                        {user.full_name || user.username}
                      </div>
                      <div className="text-xs text-[#94A3B8] text-truncate">
                        @{user.username}
                      </div>
                      <span className="inline-block mt-1 px-2 py-0.5 rounded-md bg-[#D4AF37]/15 text-[#D4AF37] text-[10px] font-bold uppercase tracking-wider border border-[#D4AF37]/30">
                        {roleLabel}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      handleLogout();
                    }}
                    className="w-full d-flex align-items-center justify-content-center gap-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/25 py-2.5 px-3 text-xs font-bold transition-all"
                    type="button"
                  >
                    <i className="bi bi-box-arrow-right fs-6" />
                    <span>Logout Account</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
